/*
 * Mialica cart drawer.
 *
 * Opens from the header cart icon ([data-mialica-cart-open]) or with
 *   document.dispatchEvent(new CustomEvent('mialica:cart:open'))
 *
 * Opens automatically after every add to cart:
 * - Product forms (form[action$="/cart/add"]) that no other script handles are sent with AJAX,
 *   with exactly the same form data (id, quantity, selling_plan, properties...).
 *   Add data-mialica-cart-native to a form to keep the normal page submit.
 * - Sections that add to cart with their own script announce it with a "cart:refresh" or
 *   "cart:updated" event whose detail contains "product" (or "items").
 * - Horizon product forms (quick add, default product template) announce it with
 *   "shopify:cart:lines-update" (action "add").
 *
 * Events fired: mialica:cart:add:start {form}, mialica:cart:add:end {form, ok, item, message},
 * mialica:cart:updated {cart}.
 */
(function () {
  'use strict';

  if (window.MialicaCartDrawer) return;

  var instance = null;
  var refreshTimer = null;
  var refreshRunning = null;
  var refreshQueued = false;
  var refreshOpen = false;
  var pendingForms = [];

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function updateHeaderCount(count) {
    document.querySelectorAll('.mh-cart-count,[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  function isCartPage() {
    return /\/cart$/.test(window.location.pathname.replace(/\/+$/, ''));
  }

  /* ---------- Drawer instance ---------- */

  function Drawer(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-mcd-drawer]');
    this.overlay = root.querySelector('[data-mcd-overlay]');
    this.sectionId = root.dataset.sectionId;
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.lastFocused = null;
    if (this.drawer) {
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
    }
    this.bind();
  }

  Drawer.prototype.isOpen = function () {
    return !!this.drawer && this.drawer.classList.contains('is-open');
  };

  Drawer.prototype.setOpenState = function (open) {
    if (!this.drawer) return;
    // Read layout first so a drawer that was just inserted still slides in.
    if (open) void this.drawer.offsetWidth;
    if (this.overlay) this.overlay.classList.toggle('is-open', open);
    this.drawer.classList.toggle('is-open', open);
    this.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';
  };

  Drawer.prototype.focusDrawer = function () {
    var drawer = this.drawer;
    if (!drawer) return;
    setTimeout(function () {
      try { drawer.focus({ preventScroll: true }); } catch (e) { drawer.focus(); }
    }, 40);
  };

  Drawer.prototype.open = function () {
    if (!this.drawer) return;
    if (!this.isOpen()) this.lastFocused = document.activeElement;
    this.setOpenState(true);
    this.focusDrawer();
  };

  Drawer.prototype.close = function () {
    if (!this.drawer) return;
    var hadFocus = this.drawer.contains(document.activeElement);
    this.setOpenState(false);
    var target = this.lastFocused;
    this.lastFocused = null;
    if (!target || target === document.body || typeof target.focus !== 'function' || !document.contains(target)) {
      // Don't leave focus inside the closed drawer.
      target = hadFocus ? document.querySelector('[data-mialica-cart-open]') : null;
    }
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
  };

  Drawer.prototype.setLoading = function (on) {
    if (!this.drawer) return;
    this.drawer.classList.toggle('is-loading', !!on);
    this.drawer.setAttribute('aria-busy', on ? 'true' : 'false');
  };

  Drawer.prototype.post = function (path, payload) {
    return fetch(this.cartUrl + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + path);
      return res.json();
    });
  };

  Drawer.prototype.changeLine = function (key, quantity) {
    var self = this;
    this.setLoading(true);
    return this.post('/change.js', { id: key, quantity: quantity })
      .then(function () { return refresh(true); })
      .catch(function (error) {
        console.error(error);
        self.setLoading(false);
      });
  };

  Drawer.prototype.addVariant = function (variantId, quantity) {
    var self = this;
    this.setLoading(true);
    return this.post('/add.js', { items: [{ id: Number(variantId), quantity: Number(quantity || 1) }] })
      .then(function () { return refresh(true); })
      .catch(function (error) {
        console.error(error);
        self.setLoading(false);
      });
  };

  Drawer.prototype.bind = function () {
    var self = this;
    var root = this.root;
    var closeBtn = root.querySelector('[data-mcd-close]');

    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });
    if (this.overlay) this.overlay.addEventListener('click', function () { self.close(); });

    root.querySelectorAll('[data-mcd-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.dataset.key;
        var qty = Number(btn.dataset.quantity);
        if (key && qty >= 0) self.changeLine(key, qty);
      });
    });

    root.querySelectorAll('[data-mcd-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.dataset.key) self.changeLine(btn.dataset.key, 0);
      });
    });

    root.querySelectorAll('[data-mcd-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-mcd-upsell-card]');
        var select = card && card.querySelector('[data-mcd-variant]');
        var variantId = select ? select.value : btn.dataset.variantId;
        if (variantId) self.addVariant(variantId, 1);
      });
    });

    var saveNote = debounce(function (textarea) {
      var state = root.querySelector('[data-mcd-note-state]');
      if (state) state.textContent = root.dataset.savingText || 'Salvataggio…';
      self.post('/update.js', { note: textarea.value })
        .then(function () {
          if (state) state.textContent = root.dataset.savedText || 'Nota salvata';
        })
        .catch(function (error) {
          console.error(error);
          if (state) state.textContent = root.dataset.errorText || 'Impossibile salvare';
        });
    }, 450);

    root.querySelectorAll('[data-mcd-note]').forEach(function (textarea) {
      textarea.addEventListener('input', function () { saveNote(textarea); });
    });

    root.addEventListener('keydown', function (event) {
      if (!self.isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusables = Array.prototype.filter.call(self.drawer.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === self.drawer)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  /* ---------- Refresh (Section Rendering API) ---------- */

  function doRefresh(open) {
    var current = instance;
    if (!current) return Promise.resolve();
    var wasOpen = current.isOpen();
    var cartUrl = current.cartUrl;
    var url = cartUrl + (cartUrl.indexOf('?') > -1 ? '&' : '?') + 'sections=' + encodeURIComponent(current.sectionId);

    // No JSON Accept header here: with it, /cart answers with the cart JSON instead of the sections.
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Cart drawer refresh failed');
        return res.json();
      })
      .then(function (data) {
        var html = data && data[current.sectionId];
        if (!html) throw new Error('Cart drawer HTML missing');
        var holder = document.createElement('div');
        holder.innerHTML = html.trim();
        var newRoot = holder.querySelector('.mialica-cart-drawer');
        if (!newRoot) throw new Error('Cart drawer root missing');
        if (!current.root.isConnected) return null;

        if (wasOpen) {
          // Keep an open drawer open without replaying the slide-in animation.
          var newDrawer = newRoot.querySelector('[data-mcd-drawer]');
          var newOverlay = newRoot.querySelector('[data-mcd-overlay]');
          if (newDrawer) {
            newDrawer.classList.add('is-open');
            newDrawer.setAttribute('aria-hidden', 'false');
          }
          if (newOverlay) newOverlay.classList.add('is-open');
        }

        var lastFocused = current.lastFocused;
        current.root.replaceWith(newRoot);
        instance = null;
        init(newRoot);
        if (instance) instance.lastFocused = lastFocused;

        if (instance && open) {
          if (wasOpen) instance.focusDrawer();
          else instance.open();
        }
        return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        updateHeaderCount(cart.item_count);
        emit('mialica:cart:updated', { cart: cart, source: 'mialica-cart-drawer' });
      });
  }

  function refresh(open) {
    if (open) refreshOpen = true;
    if (refreshRunning) {
      refreshQueued = true;
      return refreshRunning;
    }
    var wantOpen = refreshOpen;
    refreshOpen = false;
    refreshRunning = doRefresh(wantOpen)
      .catch(function (error) {
        console.error(error);
        if (instance) instance.setLoading(false);
      })
      .then(function () {
        refreshRunning = null;
        if (refreshQueued) {
          refreshQueued = false;
          return refresh(false);
        }
        return null;
      });
    return refreshRunning;
  }

  // Groups events that arrive together (e.g. cart:refresh + cart:updated) into one refresh.
  function scheduleRefresh(open) {
    if (open) refreshOpen = true;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { refresh(false); }, 60);
  }

  /* ---------- Product forms: AJAX add, then open the drawer ---------- */

  function isAddForm(form) {
    if (!form || form.nodeName !== 'FORM') return false;
    if ((form.getAttribute('method') || 'get').toLowerCase() !== 'post') return false;
    var action = (form.getAttribute('action') || '').split('?')[0].replace(/\/+$/, '');
    return /\/cart\/add$/.test(action);
  }

  function addErrorText() {
    return (instance && instance.root.dataset.addErrorText) || 'Non è stato possibile aggiungere il prodotto al carrello. Riprova.';
  }

  // Submit buttons of the form, including buttons outside it with form="..." (e.g. sticky bars).
  function formButtons(form, submitter) {
    var list = Array.prototype.filter.call(form.elements, function (el) {
      return el.type === 'submit';
    });
    if (submitter && list.indexOf(submitter) === -1) list.push(submitter);
    return list;
  }

  function setBusy(buttons, busy) {
    buttons.forEach(function (btn) {
      if (busy) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
      btn.classList.toggle('mcd-adding', busy);
    });
  }

  function errorBox(form, create) {
    var box = form.querySelector('[data-mialica-cart-error], .mp5-form-error, [data-form-error]');
    if (box || !create) return box;
    box = document.createElement('p');
    box.className = 'mcd-form-error';
    box.setAttribute('role', 'alert');
    box.setAttribute('data-mialica-cart-error', '');
    box.hidden = true;
    form.appendChild(box);
    return box;
  }

  function setFormError(form, message) {
    var box = errorBox(form, !!message);
    if (!box) return;
    box.textContent = message || '';
    box.classList.toggle('is-visible', !!message);
    if (box.hasAttribute('data-mialica-cart-error')) box.hidden = !message;
  }

  function addFromForm(form, submitter) {
    var buttons = formButtons(form, submitter);
    var cartUrl = instance ? instance.cartUrl : '/cart';
    pendingForms.push(form);
    setBusy(buttons, true);
    setFormError(form, '');
    emit('mialica:cart:add:start', { form: form });

    return fetch(cartUrl + '/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok || json.status) {
            // 422 = cart errors (e.g. not enough stock), already translated by Shopify.
            var text = res.status === 422 ? (json.description || json.message) : '';
            var failure = new Error(text || addErrorText());
            failure.shopify = true;
            throw failure;
          }
          return json;
        });
      })
      .then(function (item) {
        return refresh(true).then(function () {
          return { ok: true, item: item, message: '' };
        });
      })
      .catch(function (error) {
        var message = error && error.shopify ? error.message : addErrorText();
        if (!error || !error.shopify) console.error(error);
        setFormError(form, message);
        return { ok: false, item: null, message: message };
      })
      .then(function (result) {
        pendingForms = pendingForms.filter(function (f) { return f !== form; });
        setBusy(buttons, false);
        emit('mialica:cart:add:end', { form: form, ok: result.ok, item: result.item, message: result.message });
      });
  }

  function handleSubmit(event) {
    var form = event.target;
    // Forms already handled by their own script (they call preventDefault) are left alone.
    if (event.defaultPrevented || !instance || !isAddForm(form) || isCartPage()) return;
    if (form.hasAttribute('data-mialica-cart-native') || form.closest('product-form-component')) return;
    var submitter = event.submitter || null;
    if (submitter && submitter.hasAttribute('formaction')) return;
    event.preventDefault();
    if (pendingForms.indexOf(form) > -1) return;
    addFromForm(form, submitter);
  }

  /* ---------- Horizon product forms ---------- */

  function handleHorizonAdd(event) {
    if (event.action !== 'add' || isCartPage()) return;
    var target = event.target instanceof Element ? event.target : null;
    var modal = null;
    if (target) {
      try { modal = target.closest('dialog:modal'); } catch (e) { modal = target.closest('dialog[open]'); }
    }
    var openDrawer = function () {
      // Wait for a quick-add modal to close first, otherwise the drawer would sit under it.
      if (modal && modal.open) {
        var done = false;
        var go = function () {
          if (done) return;
          done = true;
          scheduleRefresh(true);
        };
        modal.addEventListener('close', go, { once: true });
        setTimeout(go, 1500);
      } else {
        scheduleRefresh(true);
      }
    };
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(function (result) {
        if (result && result.detail && result.detail.didError) return;
        openDrawer();
      }).catch(function () {});
    } else {
      openDrawer();
    }
  }

  /* ---------- Setup ---------- */

  function init(root) {
    if (!root || root.dataset.mcdReady === 'true') return;
    root.dataset.mcdReady = 'true';
    instance = new Drawer(root);
  }

  function initAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches('.mialica-cart-drawer')) init(context);
    context.querySelectorAll('.mialica-cart-drawer').forEach(init);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-mialica-cart-open]');
    if (!trigger || !instance) return;
    event.preventDefault();
    instance.open();
  });

  document.addEventListener('mialica:cart:open', function () {
    if (instance) instance.open();
  });

  document.addEventListener('mialica:cart:close', function () {
    if (instance) instance.close();
  });

  document.addEventListener('mialica:cart:refresh', function (event) {
    scheduleRefresh(!!(event.detail && event.detail.open));
  });

  ['cart:refresh', 'cart:updated'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      var detail = event.detail || {};
      if (String(detail.source || '').indexOf('mialica-cart-drawer') === 0) return;
      var added = !!(detail.product || detail.items || detail.item);
      scheduleRefresh(added && !isCartPage());
    });
  });

  document.addEventListener('shopify:cart:lines-update', handleHorizonAdd);
  document.addEventListener('submit', handleSubmit);

  // Back/forward cache: the cart may have changed on another page.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) scheduleRefresh(false);
  });

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    if (instance && event.target.contains(instance.root)) {
      instance.setOpenState(false);
      instance = null;
    }
  });

  window.MialicaCartDrawer = {
    open: function () { if (instance) instance.open(); },
    close: function () { if (instance) instance.close(); },
    refresh: function (open) { return refresh(!!open); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
})();
